import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import DeleteInteractionModal from '../components/DeleteInteractionModal';
import ErrorToast from '../components/ErrorToast';
import { getInteractionList } from '../services/interaction-service';
import Interaction from '../models/Interaction';
import { ContactOption } from '../models/enums/ContactOption';
import {useDebounce} from "../utils/CommonUtils";
import InteractionSearchParams from "../models/InteractionSearchParams";
import {formatInteractionTime} from "../utils/DateTimeUtils";

const ClientHistoryPage: React.FC = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const navigate = useNavigate();
    const numericClientId = Number(clientId);

    const [interactions, setInteractions] = useState<Interaction[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const debouncedSearch = useDebounce(searchTerm, 500);
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [selectedTypes, setSelectedTypes] = useState<ContactOption[]>([]);
    const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('DESC');
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [errorToast, setErrorToast] = useState<string>('');

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [selectedInteractionId, setSelectedInteractionId] = useState<number>(0);

    useEffect(() => {
        const fetchInteractions = async () => {
            if (debouncedSearch && debouncedSearch.length < 3) {
                return;
            }
            try {
                const params: InteractionSearchParams = {
                    clientId: numericClientId,
                    page: page,
                    pageSize: pageSize,
                    search: debouncedSearch,
                    dateFrom: dateFrom,
                    dateTo: dateTo,
                    types: selectedTypes.length > 0 ? selectedTypes : null,
                    sortBy: 'interactionDate',
                    sortDir: sortDir,
                };
                const response = await getInteractionList(params);
                if (response.status === 200 && response.data) {
                    setInteractions(response.data.interactions);
                    setTotalCount(response.data.totalCount);
                } else {
                    setErrorToast('Unable to fetch interactions. Please try again later.');
                }
            } catch (err) {
                setErrorToast('Unable to fetch interactions. Please try again later.');
            }
        };

        fetchInteractions();
    }, [numericClientId, page, pageSize, debouncedSearch, dateFrom, dateTo, selectedTypes, sortDir]);

    const handleToggleType = (type: ContactOption) => {
        setSelectedTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
        setPage(1);
    };

    const resetFilters = () => {
        setDateFrom('');
        setDateTo('');
        setSelectedTypes([]);
        setPage(1);
    };

    const handleDelete = (id: number) => {
        setInteractions(prev => prev.filter(i => i.id !== id));
        setTotalCount(prev => prev - 1);
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-grow container mx-auto px-4 py-6">
                {/* Top controls */}
                <button
                    data-testid="back-btn"
                    onClick={() => navigate(`/client/${clientId}`)}
                    className="text-crumbblue hover:underline mb-4"
                >
                    &larr; Back to Client
                </button>
                <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                    <div className="flex items-center space-x-2">
                        <button
                            data-testid="add-interaction-btn"
                            className="bg-crumborange text-white px-4 py-2 rounded hover:bg-orange-600"
                            onClick={() => navigate(`/interaction/new/${numericClientId}`)}
                        >
                            Add Interaction
                        </button>
                        <input
                            type="text"
                            placeholder="Search interactions..."
                            className="border rounded p-2"
                            value={searchTerm}
                            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                        />
                    </div>
                    <div className="flex items-center space-x-2">
                        <label>Show:</label>
                        <select
                            data-testid="page-size"
                            value={pageSize}
                            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            className="border rounded p-1"
                        >
                            {[10,20,50,100].map(size => (
                                <option key={size} value={size}>{size} per page</option>
                            ))}
                        </select>
                        <label>Sort:</label>
                        <select
                            data-testid="sort-by"
                            value={sortDir}
                            onChange={e => setSortDir(e.target.value as 'ASC' | 'DESC')}
                            className="border rounded p-1"
                        >
                            <option value="ASC">Date Ascending</option>
                            <option value="DESC">Date Descending</option>
                        </select>
                        <button
                            data-testid="filter-btn"
                            className={`border rounded p-2 ${showFilters ? 'bg-crumborange text-white' : 'bg-white'}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            Filters
                        </button>
                    </div>
                </div>

                {/* Filters panel */}
                {showFilters && (
                    <div className="border rounded p-4 mb-4" data-testid="filter-panel">
                        <div className="grid md:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="date-from" className="block mb-1">Date From</label>
                                <input
                                    id="date-from"
                                    type="date"
                                    className="border rounded w-full p-2"
                                    max={dateTo}
                                    value={dateFrom}
                                    onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                                />
                            </div>
                            <div>
                                <label htmlFor="date-to" className="block mb-1">Date To</label>
                                <input
                                    id="date-to"
                                    type="date"
                                    className="border rounded w-full p-2"
                                    value={dateTo}
                                    min={dateFrom}
                                    onChange={e => { setDateTo(e.target.value); setPage(1); }}
                                />
                            </div>
                            <div>
                                <label className="block mb-1">Type</label>
                                <div className="flex flex-wrap gap-2">
                                    {Object.values(ContactOption).map(opt => (
                                        <button
                                            key={opt}
                                            data-testid={`${opt}-btn`}
                                            type="button"
                                            className={`px-3 py-1 border rounded cursor-pointer ${
                                                selectedTypes.includes(opt) ? 'bg-crumborange text-white' : 'bg-white'
                                            }`}
                                            onClick={() => handleToggleType(opt)}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="mt-4">
                            <button
                                data-testid='reset-filters'
                                className="px-4 py-2 border rounded"
                                onClick={resetFilters}
                            >
                                Reset Filters
                            </button>
                        </div>
                    </div>
                )}

                {/* Data table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border rounded">
                        <thead className="bg-gray-100 text-left">
                        <tr>
                            <th className="px-4 py-2" data-testid="Subject-header">Subject</th>
                            <th className="px-4 py-2" data-testid="Date-header">Date</th>
                            <th className="px-4 py-2" data-testid="Time-header">Time</th>
                            <th className="px-4 py-2" data-testid="Type-header">Type</th>
                            <th className="px-4 py-2" data-testid="Outcomes-header">Outcome</th>
                            <th className="px-4 py-2" data-testid="Tags-header">Tags</th>
                            <th className="px-4 py-2" data-testid="Actions-header">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {interactions.length > 0 ? (
                            interactions.map(interaction => {
                                const timeDisplay = formatInteractionTime(interaction);
                                return (
                                    <tr
                                        data-testid={`interaction-${interaction.id}`}
                                        key={interaction.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => navigate(`/interaction/${interaction.id}`)}
                                    >
                                        <td className="px-4 py-2">{interaction.subject}</td>
                                        <td className="px-4 py-2">{interaction.interactionDate.toLocaleDateString()}</td>
                                        <td className="px-4 py-2" data-testid={`time-${interaction.id}`} >{timeDisplay ?? '—'}</td>
                                        <td className="px-4 py-2">{interaction.type}</td>
                                        <td className="px-4 py-2">{interaction.outcome}</td>
                                        <td className="px-4 py-2">
                                            {interaction.tags && interaction.tags.length > 0
                                                ? interaction.tags.join(', ')
                                                : ''}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            <div className="flex space-x-2">
                                                <button
                                                    className="text-crumbblue hover:underline"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        navigate(`/interaction/update/${interaction.id}`);
                                                    }}
                                                    data-testid={`edit-interaction-${interaction.id}`}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="text-red-600 hover:underline"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setSelectedInteractionId(interaction.id!);
                                                        setIsDeleteModalOpen(true);
                                                    }}
                                                    data-testid={`delete-interaction-${interaction.id}`}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={6} className="p-4 text-center text-gray-500">
                                    No interactions found.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="flex justify-between items-center mt-4">
                    <div data-testid='page-range'>
                        {`Showing ${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, totalCount)} of ${totalCount}`}
                    </div>
                    <div className="space-x-2">
                        <button
                            onClick={() => setPage(p => Math.max(p - 1, 1))}
                            disabled={page === 1}
                            className="px-3 py-1 border rounded disabled:opacity-50"
                        >
                            Prev
                        </button>
                        <button
                            onClick={() => setPage(p => p + 1)}
                            disabled={page * pageSize >= totalCount}
                            className="px-3 py-1 border rounded disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                </div>

                {/* Delete Modal */}
                {isDeleteModalOpen && (
                    <DeleteInteractionModal
                        isOpen={isDeleteModalOpen}
                        interactionId={selectedInteractionId}
                        onClose={() => setIsDeleteModalOpen(false)}
                        onDelete={(interactionId) => handleDelete(interactionId)}
                        onError={message => setErrorToast(message)}
                    />
                )}

                {/* Error Toast */}
                <ErrorToast
                    message={errorToast}
                    clearError={() => setErrorToast('')}
                />

            </main>
            <Footer />
        </div>
    );
};

export default ClientHistoryPage;
